// @ts-nocheck
import { useState, useEffect } from 'react'
import { useLeaguePaymentForm } from '@/hooks/useLeaguePaymentForm'
import { Button } from "@/components/ui/button"
import SidePanel from './SidePanel'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { ExternalLink } from "lucide-react"
import helpers from '@/utils/helpers'

// PaymentProducts describe their kind via boolean flags (perTeam / perPlayer /
// isFree / isDonation), not a single `type` string — derive a human label.
const describeType = (p?: any): string => {
  if (!p) return ''
  if (p.isDonation) return 'Donation'
  if (p.perPlayer) return p.playerPaySeparately ? 'Per player · each pays their own' : 'Per player'
  if (p.perTeam) return 'Per team'
  if (p.isFree || !p.amount) return 'Free'
  return 'Per team'
}

const formatAmount = (amount?: number | null) =>
  amount ? `$${(amount / 100).toFixed(2)}` : 'Free'

const describeProduct = (p?: any): string =>
  [describeType(p), p?.amount ? formatAmount(p.amount) : null].filter(Boolean).join(' · ')

// `currentProduct` here is actually the ENTITY being edited (tournament / league
// / division). Its assigned product is `entity.config.paymentProduct` (ADR 0034
// — the fee is a config-hierarchy field) — a ref that may be a bare ObjectId or
// a populated object, so unwrap with helpers.getObjId.
export default function PaymentModal({ open, onClose, title = 'Payment', subtitle, onSubmit, products, currentProduct: entity }) {
  const [changesMade, setChangesMade] = useState(false)
  const { selectedPayment, setSelectedPayment, activeTab, setActiveTab } = useLeaguePaymentForm()

  const activePayments = (products || []).filter((p) => !p.deactivated)
  const currentProductId = entity?.config?.paymentProduct ? helpers.getObjId(entity.config.paymentProduct) : null
  const currentProduct = currentProductId
    ? (products || []).find((p) => p._id === currentProductId) || null
    : null

  // On open, pre-select the entity's currently-assigned product so it shows as
  // selected in the list.
  useEffect(() => {
    setSelectedPayment(currentProduct)
    setChangesMade(false)
  }, [currentProductId, open])

  const onSubmitSuccess = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedPayment) return
    onSubmit({ selectedPayment })
    onClose()
  }

  const footer =
    activeTab === 'select' ? (
      <Button
        type="submit"
        form="payment-form"
        className="w-full"
        disabled={!selectedPayment || !changesMade}
      >
        Use This Payment
      </Button>
    ) : null

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      description="Choose which payment option applies. Click save when you're done."
      footer={footer}
    >
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'select' | 'create')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="select">Select Payment</TabsTrigger>
          <TabsTrigger value="create">Create New</TabsTrigger>
        </TabsList>

        {/* ── Select an existing payment ── */}
        <TabsContent value="select" className="mt-4">
          <form id="payment-form" onSubmit={onSubmitSuccess} className="space-y-4">
            {activePayments.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No saved payment options yet.{' '}
                <button
                  type="button"
                  className="font-medium text-primary underline underline-offset-2"
                  onClick={() => setActiveTab('create')}
                >
                  Create one
                </button>{' '}
                to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {activePayments.map((payment) => {
                  const selected = selectedPayment?._id === payment._id
                  const isCurrent = payment._id === currentProductId
                  return (
                    <button
                      type="button"
                      key={payment._id}
                      onClick={() => {
                        setSelectedPayment(payment)
                        setChangesMade(payment._id !== currentProductId)
                      }}
                      className={cn(
                        "w-full rounded-lg border p-4 text-left transition-colors hover:bg-accent",
                        selected && "border-primary bg-accent ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2">
                          <span className="text-base font-medium">{payment.name}</span>
                          {isCurrent && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                              Current
                            </span>
                          )}
                        </span>
                        <span className="text-base font-semibold">{formatAmount(payment.amount)}</span>
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">{describeType(payment)}</p>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Current vs. selected summary */}
            <div className="space-y-1 rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">
                Current:{' '}
                <span className="font-medium text-foreground">{currentProduct?.name || 'None'}</span>
                {currentProduct ? ` · ${describeProduct(currentProduct)}` : ''}
              </p>
              {selectedPayment && selectedPayment._id !== currentProductId && (
                <p className="text-sm text-muted-foreground">
                  New:{' '}
                  <span className="font-medium text-foreground">{selectedPayment.name}</span>
                  {` · ${describeProduct(selectedPayment)}`}
                </p>
              )}
            </div>
          </form>
        </TabsContent>

        {/* ── Create a new payment (managed on the Payment Options page) ── */}
        <TabsContent value="create" className="mt-4">
          <div className="space-y-4 rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Create and manage payment options (pricing, per-team / per-player fees,
              advanced tiers and more) on each registration's Payments pane
              (Registrations → Edit → Payments). New options appear in the
              Select Payment list here.
            </p>
            <Button asChild variant="outline">
              <a href="/admin/registration" target="_blank" rel="noopener noreferrer">
                Open Registrations
                <ExternalLink className="ml-1.5 h-4 w-4" />
              </a>
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </SidePanel>
  )
}
